import { Fragment, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { lies } from "../api/client";
import type { Treffer } from "../api/types";
import { datum } from "../lib/format";
import { Meldung } from "./Bausteine";

const ART: Record<Treffer["art"], string> = {
  protokoll: "Protokoll", mangel: "Mangel", begehung: "Begehung", tagebuch: "Bautagebuch", einwand: "Einwand",
};

/** Suche über alle Unterlagen des Projekts (SPEC §4.10). Das Feld steht im Kopf, die
 *  Treffer darunter — sie verdecken die Reiter, bis die Suche geleert wird. */
export function useSuche(projektId: string) {
  const [q, setQ] = useState("");
  const [wort, setWort] = useState("");
  // Erst nach einer kurzen Tipppause fragen, nicht bei jedem Buchstaben.
  useEffect(() => {
    const t = setTimeout(() => setWort(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const treffer = useQuery({
    queryKey: ["suche", projektId, wort],
    queryFn: () => lies<Treffer[]>(`/projekte/${projektId}/suche?q=${encodeURIComponent(wort)}`),
    enabled: !!projektId && wort.length >= 2,
  });
  return { q, setQ, wort, treffer, aktiv: wort.length >= 2 };
}

export function SuchFeld({ q, setQ }: { q: string; setQ: (v: string) => void }) {
  return (
    <label className="suche">
      <MagnifyingGlass size={16} />
      <input className="eingabe" type="search" placeholder="Im Projekt suchen" aria-label="Im Projekt suchen"
             value={q} onChange={(e) => setQ(e.target.value)}
             onKeyDown={(e) => e.key === "Escape" && setQ("")} />
    </label>
  );
}

export function SuchTreffer({ s }: { s: ReturnType<typeof useSuche> }) {
  if (!s.aktiv) return null;
  const liste = s.treffer.data;
  return (
    <section className="karte treffer-liste">
      <div className="karte-kopf">
        <h2>Suche „{s.wort}“</h2>
        <span className="mute">{liste ? `${liste.length} Treffer` : "sucht …"}</span>
        <div className="luft" />
        <button className="knopf leise symbol" aria-label="Suche leeren" onClick={() => s.setQ("")}><X size={16} /></button>
      </div>
      <Meldung fehler={s.treffer.error} />
      {liste?.map((t, i) => (
        <Link key={i} to={t.link} className="treffer">
          <div className="treffer-kopf">
            <span className="chip">{ART[t.art]}</span>
            <b>{t.titel}</b>
            <span className="mute">{datum(t.datum)}</span>
          </div>
          <div className="mute"><Markiert text={t.auszug} wort={s.wort} /></div>
        </Link>
      ))}
      {liste?.length === 0 && <div className="leer">Nichts gefunden.</div>}
    </section>
  );
}

function Markiert({ text, wort }: { text: string; wort: string }) {
  const rx = new RegExp(`(${wort.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return <>{text.split(rx).map((t, i) => (i % 2 ? <mark key={i}>{t}</mark> : <Fragment key={i}>{t}</Fragment>))}</>;
}
